import React, { CSSProperties } from 'react';
import styled from 'styled-components';
import { WrapperVariant } from '@material-ui/pickers/wrappers/Wrapper';
import { isValidDate } from '../../utils/dateUtil';
import { ParsableDate } from '../MaterialPickersUtils';
import { Uds } from '../../types';
import { DatePickerAdapter, DatePickerAdapterProps } from './DatePickerAdapter';

export interface DatePickerProps extends Uds<DatePickerAdapterProps> {
  onChange(date: Date | null): void;
  onAccept?(date: Date | null): void;
  onClose?(): void;
  onOpen?(): void;
  onError?(error?: any): void;
  dateTime?: boolean;
  clearable?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: ParsableDate;
  maxDate?: ParsableDate;
  inputStyle?: CSSProperties;
  value: ParsableDate;
  placeholder?: string;
  id?: string;
  variant?: WrapperVariant;
  minDateMessage?: string;
  maxDateMessage?: string;
}

export const DatePicker: React.FC<React.PropsWithChildren<DatePickerProps>> = ({
  clearable = true,
  value = null,
  inputStyle,
  onChange,
  onAccept,
  onClose,
  onOpen,
  onError,
  dateTime,
  placeholder,
  autoFocus,
  disabled,
  fullWidth,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  variant,
  dataTest,
  id,
  maxDateMessage,
  minDateMessage,
  ...rest
}) => (
  <StyledContainer $fullWidth={fullWidth}>
    <DatePickerAdapter
      disabled={disabled}
      dateTime={dateTime}
      keyboard
      autoOk
      minDate={minDate}
      minDateMessage={minDateMessage}
      maxDate={maxDate}
      maxDateMessage={maxDateMessage}
      data-test={dataTest ?? 'date-picker'}
      clearable={clearable}
      value={value}
      id={id}
      variant={variant}
      disablePast={disablePast}
      disableFuture={disableFuture}
      onChange={(date: Date | null) => {
        if (!date || isValidDate(date)) {
          onChange(date);
        }
      }}
      onError={(reason) => {
        if (reason) {
          onError?.(reason);
        } else {
          onError?.('');
        }
      }}
      onAccept={onAccept}
      onClose={onClose}
      onOpen={onOpen}
      InputProps={{
        disableUnderline: true,
        placeholder,
        autoFocus,
        disabled,
        style: {
          ...inputStyle
        }
      }}
      {...rest}
    />
  </StyledContainer>
);

export default DatePicker;

const StyledContainer = styled.span<{ $fullWidth?: boolean }>`
  [data-test-id='date-picker'],
  [data-test='date-picker'] {
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  }
`;
